import { PointsInput } from "@/features/korrektur/components/pointsInput.component";
import type { KorrekturKorrekturbogenItem } from "@/features/korrektur/korrektur.types";
import EditableText from "@/components/EditableText";
import { Button } from "@/components/ui/button";
import { ArrowUpDown, Trash2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useOrderingModal } from "@/features/klausur/context/orderingModalProvider";
import { DestructiveDialog } from "@/components/destructive-dialog.component";
import { useAuth } from "@/lib/contexts/auth.context";
import { KlausurService } from "@/lib/klausur/klausur.service";
import { useKlausurEditing } from "@/features/klausur/context/klausurEditingProvider";

// Half-point helpers at module scope to avoid re-creation and hook dep noise
const toHalfUnits = (n: number | null | undefined) =>
	Math.round(((n ?? 0) as number) * 2);
const fromHalfUnits = (u: number) => u / 2;

// Pure helpers hoisted to module scope to keep stable identity for hooks
const stripFontSize = (content: string) =>
	content.replace(/font-size:\s*\d+(\.\d+)?(pt|px);?/g, "");

interface KlausurErstellerKorrekturbogenEditorProps {
	title: string;
	korrekturbogenItems: KorrekturKorrekturbogenItem[];
	musterloesungId: string;
	onMusterloesungTitleUpdate?: (musterloesungId: string, title: string) => void;
	onItemMaxPointsUpdate?: (
		korrekturbogenItemId: string,
		maxPoints: number,
	) => void;
	onItemTextUpdate?: (korrekturbogenItemId: string, text: string) => void;
	canEditStructure?: boolean;
	/** Optional: register the root element of this musterloesung for scrolling */
	onRegisterMusterloesungRef?: (el: HTMLElement | null) => void;
	/** Optional: register each item element for scrolling */
	onRegisterItemRef?: (id: string, el: HTMLElement | null) => void;
}

/**
 * klausurErstellerKorrekturbogenEditor - A component that displays a list of korrekturbogen items so user can edit own points
 *
 * This component displays a list of korrekturbogen items, each with a title,
 * a list of parsed items, and a user point input for each item.
 *
 * @param {korrekturbogenItems} - The list of korrekturbogen items to display
 * @param {title} - The title of the korrekturbogen
 * @param {musterloesungId} - The id of the musterloesung
 * @param {onMusterloesungTitleUpdate} - A callback function that is called when the musterloesung title is updated
 * @param {onItemMaxPointsUpdate} - A callback function that is called when the user points are updated
 * @param {onItemTextUpdate} - A callback function that is called when the user text is updated
 * @param {canEditStructure} - A boolean that indicates if the user can edit the structure
 * @param {onRegisterMusterloesungRef} - A callback function that is called when the musterloesung ref is registered
 * @param {onRegisterItemRef} - A callback function that is called when the item ref is registered
 * @returns {JSX.Element} The rendered component
 */
export default function KlausurErstellerKorrekturbogenEditor({
	korrekturbogenItems,
	title,
	musterloesungId,
	onMusterloesungTitleUpdate,
	onItemMaxPointsUpdate,
	onItemTextUpdate,
	canEditStructure = false,
	onRegisterMusterloesungRef,
	onRegisterItemRef,
}: KlausurErstellerKorrekturbogenEditorProps) {
	const [items, setItems] = useState<KorrekturKorrekturbogenItem[]>([]);
	const [totalPoints, setTotalPoints] = useState(0);
	const { openKorrekturbogenItemsOrdering } = useOrderingModal();
	const { currentOrganization } = useAuth();
	const { klausurId, reloadKlausur } = useKlausurEditing();
	const [isDeleteOpen, setIsDeleteOpen] = useState(false);
	const [deletingItem, setDeletingItem] = useState<{
		id: string;
		label: string;
	} | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	// Update local state when props change
	useEffect(() => {
		setItems(korrekturbogenItems);
	}, [korrekturbogenItems]);

	// Calculate total points (sum of maxPoints) with half-point rounding
	useEffect(() => {
		// Sum maxPoints in half-point units, then convert back
		const totalUnits = items.reduce(
			(sum, item) => sum + toHalfUnits(item.maxPoints),
			0,
		);
		const total = fromHalfUnits(totalUnits);
		setTotalPoints(total);
	}, [items]);

	// This function removes the font-size style from the content
	const formatContent = (content: string) => {
		try {
			return stripFontSize(content);
		} catch (error) {
			console.error("Error processing HTML:", error);
			return content; // Return original content if processing fails
		}
	};

	/**
	 * Convert HTML to plain text
	 * @param html - The HTML to convert
	 * @returns The plain text
	 */
	const toPlainText = useCallback((html: string) => {
		try {
			const processed = stripFontSize(html)
				.replace(/<br\s*\/?>(\n)?/gi, " ")
				.replace(/<[^>]+>/g, "");
			const trimmed = processed.trim().replace(/\s+/g, " ");
			return trimmed.length > 100 ? trimmed.slice(0, 100) + "…" : trimmed;
		} catch (_) {
			return html;
		}
	}, []);

	const openDeleteDialog = useCallback(
		(item: KorrekturKorrekturbogenItem) => {
			setDeletingItem({
				id: item.id,
				label: toPlainText(item.text) || "(leer)",
			});
			setIsDeleteOpen(true);
		},
		[toPlainText],
	);

	const handleConfirmDelete = useCallback(async () => {
		if (!currentOrganization?.organizationId || !klausurId || !deletingItem)
			return;
		setIsDeleting(true);
		try {
			await KlausurService.getInstance().deleteComponent(
				currentOrganization.organizationId,
				klausurId,
				"korrekturbogenitem",
				musterloesungId,
				deletingItem.id,
			);
			// Optimistic local remove
			setItems((prev) => prev.filter((i) => i.id !== deletingItem.id));
			setIsDeleteOpen(false);
			setDeletingItem(null);
			await reloadKlausur();
		} finally {
			setIsDeleting(false);
		}
	}, [
		currentOrganization?.organizationId,
		klausurId,
		deletingItem,
		musterloesungId,
		reloadKlausur,
	]);

	return (
		<div
			className="flex flex-col h-full overflow-y-auto scrollbar-thin scrollbar-thumb-border scrollbar-track-transparent"
			ref={(el) => onRegisterMusterloesungRef?.(el)}
		>
			<div className="flex flex-col bg-backgroundSecondary rounded-lg last:mb-0">
				<div className="px-3 py-2 flex justify-between items-center sticky top-0 bg-gray-200 z-5 pr-4 @container min-w-0">
					<h2 className="font-semibold truncate flex-1 min-w-0 mr-2">
						{canEditStructure ? (
							<EditableText
								value={title}
								placeholder="Musterlösungstitel…"
								className="font-semibold w-full"
								multiline={false}
								onCommit={(val) =>
									onMusterloesungTitleUpdate?.(musterloesungId, val)
								}
							/>
						) : (
							<span>{title}</span>
						)}
					</h2>
					<div className="flex items-center gap-2 shrink-0">
						<div className="flex flex-col @[400px]:flex-row items-end @[400px]:items-center gap-0.5 @[400px]:gap-2">
							<span className="text-muted-foreground text-xs">max</span>
							<div className="font-semibold">{totalPoints}</div>
						</div>
						<Button
							variant="ghost"
							size="sm"
							onClick={() =>
								openKorrekturbogenItemsOrdering(
									items
										.slice()
										.sort((a, b) => a.index - b.index)
										.map((it) => ({
											id: it.id,
											label: toPlainText(it.text) || "(leer)",
											index: it.index,
										})),
								)
							}
							title="Reihenfolge der Elemente ändern"
							className="h-8 px-2"
						>
							<ArrowUpDown className="h-4 w-4" />
						</Button>
					</div>
				</div>

				{/* Individual Items */}
				{items.map((korrekturbogenItem) => (
					<div
						className="p-3 border-b border-dashed last:border-b-0"
						key={korrekturbogenItem.id}
					>
						<div
							className="flex items-center justify-between @container min-w-0"
							ref={(el) => onRegisterItemRef?.(korrekturbogenItem.id, el)}
						>
							<div className="font-medium leading-tight prose prose-xs max-w-none flex-1 mr-4 min-w-0 overflow-hidden">
								{canEditStructure ? (
									<EditableText
										value={korrekturbogenItem.text}
										placeholder="Hier tippen..."
										className="w-full"
										onCommit={(val) =>
											onItemTextUpdate?.(korrekturbogenItem.id, val)
										}
										useTaxpeerEditor={true}
										editorId={`korrekturbogen-item-${korrekturbogenItem.id}`}
									/>
								) : (
									<span className="block truncate">
										{formatContent(korrekturbogenItem.text)}
									</span>
								)}
							</div>
							<div className="flex flex-col @[400px]:flex-row items-end @[400px]:items-center gap-0.5 @[400px]:gap-1 shrink-0">
								<div className="flex items-center gap-1 justify-center">
									<span className="text-muted-foreground text-xs">max</span>
									{canEditStructure ? (
										<PointsInput
											id={`maxPoints-${korrekturbogenItem.id}`}
											value={korrekturbogenItem.maxPoints}
											min={0}
											max={999}
											step={0.5}
											onChange={(val) =>
												onItemMaxPointsUpdate?.(korrekturbogenItem.id, val)
											}
											className="font-medium min-w-6"
										/>
									) : (
										<span className="font-medium min-w-6">
											{korrekturbogenItem.maxPoints}
										</span>
									)}
									{canEditStructure && (
										<Button
											variant="ghost"
											size="icon"
											title="Element löschen"
											onClick={() => openDeleteDialog(korrekturbogenItem)}
											className="h-8 w-8"
										>
											<Trash2 className="h-4 w-4" />
										</Button>
									)}
								</div>
							</div>
						</div>
					</div>
				))}
				<DestructiveDialog
					isOpen={isDeleteOpen}
					title="Korrekturbogen-Element löschen?"
					description={`Möchtest du "${deletingItem?.label ?? ""}" wirklich löschen?`}
					warning="Diese Aktion kann nicht rückgängig gemacht werden."
					onConfirm={() => {
						if (!isDeleting) void handleConfirmDelete();
					}}
					onCancel={() => {
						if (isDeleting) return;
						setIsDeleteOpen(false);
						setDeletingItem(null);
					}}
					confirmLabel={isDeleting ? "Löschen…" : "Löschen"}
				/>
			</div>
		</div>
	);
}
